import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get(
      'payment_intent_client_secret'
    );

    if (!paymentIntentClientSecret) {
      setStatus('error');
      setError(t('payment.missing_client_secret'));
      return;
    }

    // Simuler un délai de traitement
    const timer = setTimeout(() => {
      // Dans un cas réel, vous devriez vérifier le statut du paiement avec votre backend
      // Pour l'instant, nous supposons que le paiement est réussi si nous avons un payment_intent
      if (paymentIntent) {
        setStatus('success');
      } else {
        setStatus('error');
        setError(t('payment.payment_failed'));
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [searchParams, t]);

  const handleReturnHome = () => {
    // Get the blueprintId from the URL parameters
    const blueprintId = searchParams.get('blueprintId');
    if (blueprintId) {
      navigate(`/blueprint/${blueprintId}`);
    } else {
      navigate('/');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {status === 'processing' && (
          <>
            <CircularProgress sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              {t('payment.processing_payment')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('payment.please_wait')}
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <Typography variant="h4" color="success.main" gutterBottom>
              {t('payment.success_title')}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {t('payment.success_message')}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {t('payment.success_details')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleReturnHome}
              sx={{ mt: 2 }}
            >
              {t('payment.return_to_blueprint')}
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Typography variant="h4" color="error.main" gutterBottom>
              {t('payment.error_title')}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Typography variant="body1" color="text.secondary" paragraph>
              {t('payment.error_details')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleReturnHome}
              sx={{ mt: 2 }}
            >
              {t('payment.return_to_blueprint')}
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PaymentSuccessPage;
